import { Helmet } from '@dr.pogodin/react-helmet';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import PageNotFound from '../common/lib/PageNotFound';
import type { MultiLang } from '../config';
import Functions from '../functions';
import { INDEX_ID_PUBLIC } from './lib/IndexUtil';
import ItemUtil from './lib/ItemUtil';
import styles from './Xoonips.module.css';
import XoonipsAdvancedSearch from './XoonipsAdvancedSearch';
import XoonipsDetailItem from './XoonipsDetailItem';
import XoonipsSearchByAdvancedKeyword from './XoonipsSearchByAdvancedKeyword';
import XoonipsSearchByIndexId from './XoonipsSearchByIndexId';
import XoonipsSearchByItemType from './XoonipsSearchByItemType';
import XoonipsSearchByKeyword from './XoonipsSearchByKeyword';
import XoonipsTop from './XoonipsTop';

interface Props {
    lang: MultiLang;
}

/*
 * The pages keep the addresses of the original XooNIps 3 module
 * (`detail.php?item_id=N`, `listitem.php?index_id=N`, `itemselect.php?op=...`),
 * so the page is chosen by the script name and its query string.
 */

const ItemDetail = (props: Props) => {
    const { lang } = props;
    const { search } = useLocation();
    const params = new URLSearchParams(search);
    const itemIdStr = params.get('item_id');
    const itemId = itemIdStr !== null ? (/^\d+$/.test(itemIdStr) ? parseInt(itemIdStr, 10) : 0) : null;
    return <XoonipsDetailItem lang={lang} id={itemId} doi={params.get('id')} />;
};

const ItemList = (props: Props) => {
    const { lang } = props;
    const { search } = useLocation();
    const id = new URLSearchParams(search).get('index_id') ?? '';
    return <XoonipsSearchByIndexId lang={lang} id={/^\d+$/.test(id) ? parseInt(id, 10) : INDEX_ID_PUBLIC} />;
};

const ItemSelect = (props: Props) => {
    const { lang } = props;
    const { search } = useLocation();
    const params = new URLSearchParams(search);
    const itemTypeOf = (value: string | null) => value?.match(/^xnp([a-z]+)$/)?.[1] ?? '';
    switch (params.get('op') ?? '') {
        case 'quicksearch': {
            const { type, keyword } = ItemUtil.getSearchKeywordByQuery(search);
            return <XoonipsSearchByKeyword lang={lang} type={type} keyword={keyword} />;
        }
        case 'itemtypesearch':
            return (
                <XoonipsSearchByItemType
                    lang={lang}
                    itemType={itemTypeOf(params.get('search_itemtype'))}
                    itemSubType=""
                />
            );
        case 'itemsubtypesearch': {
            // XooNIps 3 names the type as `xnp<type>=on` and its sub type as `xnp<type>_...`.
            let itemType = '';
            params.forEach((v, k) => {
                if (itemType === '' && /^xnp[a-z]+$/.test(k) && v === 'on') {
                    itemType = k.replace(/^xnp/, '');
                }
            });
            let itemSubType = '';
            if (itemType !== '') {
                params.forEach((v, k) => {
                    if (itemSubType === '' && new RegExp(`^xnp${itemType}_.+$`).test(k) && v !== '') {
                        itemSubType = v;
                    }
                });
            } else {
                itemType = itemTypeOf(params.get('search_itemtype'));
            }
            if (itemSubType === '') {
                itemSubType = params.get('search_subitemtype') ?? '';
            }
            return <XoonipsSearchByItemType lang={lang} itemType={itemType} itemSubType={itemSubType} />;
        }
        case 'advanced':
            return <XoonipsSearchByAdvancedKeyword lang={lang} />;
    }
    return <PageNotFound lang={lang} />;
};

const Xoonips = (props: Props) => {
    const { lang } = props;
    return (
        <div className={styles.database}>
            <Helmet>
                <title>
                    {Functions.mlang('[en]Database[/en][ja]データベース[/ja]', lang)} - {Functions.siteTitle(lang)}
                </title>
            </Helmet>
            <Routes>
                <Route index element={<XoonipsTop lang={lang} />} />
                <Route path="index.php" element={<Navigate to="/modules/xoonips/" replace />} />
                <Route path="detail.php" element={<ItemDetail lang={lang} />} />
                <Route path="listitem.php" element={<ItemList lang={lang} />} />
                <Route path="itemselect.php" element={<ItemSelect lang={lang} />} />
                <Route path="advanced_search.php" element={<XoonipsAdvancedSearch lang={lang} />} />
                <Route path="search/advanced" element={<XoonipsSearchByAdvancedKeyword lang={lang} />} />
                <Route path="*" element={<PageNotFound lang={lang} />} />
            </Routes>
        </div>
    );
};

export default Xoonips;
